import { useEffect } from 'react';

import { useSearchParams } from 'react-router-dom';

import STATES from '@zenbusiness/graphql-commons-states/static/states';

import useFlowState from '#client/hooks/flow/useFlowState';
import { sanitize, isURL } from '#client/utils/dataValidation';
import decodeAndSanitizeJSON from '#client/utils/dataValidation/decodeAndSanitizeJSON';

const ALLOWED_AUDIENCE_TYPES = new Set(['facebook', 'icp', 'prospecting', 'sab', 'tiktok']);
const ALLOWED_STATES = new Set(STATES.map(({ value }) => value));

const pickers = {
  domesticJurisdiction(sanitizedValue, set, get) {
    const jurisdictionState = sanitizedValue.toUpperCase();
    const businessInfo = get('businessInfo');

    if (
      jurisdictionState &&
      ALLOWED_STATES.has(jurisdictionState) &&
      !businessInfo?.jurisdictionState
    ) {
      set('businessInfo.jurisdictionState', jurisdictionState);
    }
  },
  name(sanitizedValue, set, get) {
    const businessInfo = get('businessInfo');
    if (sanitizedValue && !businessInfo?.name) {
      set('businessInfo.name', sanitizedValue);
    }
  },
  entry_uri(sanitizedValue, set) {
    set('trackingParams.entryUri', decodeURIComponent(sanitizedValue));
  },
  audience_type(sanitizedValue, set) {
    const audience = sanitizedValue.toLowerCase();
    const isValid = ALLOWED_AUDIENCE_TYPES.has(audience);
    set('trackingParams.audienceType', isValid ? audience : '');
  },
  affiliate(sanitizedValue, set, get) {
    const trackingParams = get('trackingParams');
    if (sanitizedValue && !trackingParams?.affiliate) {
      set('trackingParams.affiliate', sanitizedValue);
    }
  },
  business_website(sanitizedValue, set, get) {
    const businessInfo = get('businessInfo');
    let website = sanitizedValue;
    if (isURL(website) && !businessInfo?.website) {
      if (website && !website.startsWith('http://') && !website.startsWith('https://')) {
        website = `https://${website}`;
      }
      set('businessInfo.website', website);
    }
  },
  info(sanitizedValue, set) {
    const decodedData = decodeAndSanitizeJSON(sanitizedValue);
    if (typeof decodedData !== 'object') {
      throw new Error('Invalid JSON data - object is expected');
    }
    const { businessName, entityType, jurisdictionCode, firstName, lastName, email, phone } =
      decodedData;

    // Update business info
    set('businessInfo', {
      entityType,
      jurisdictionState: jurisdictionCode,
      name: businessName
    });

    // Update contact info
    set('contactInfo', {
      email,
      firstName,
      lastName,
      phone
    });
  },
  extole_campaign: true,
  extole_event_name: true,
  extole_label: true,
  extole_shareable_code: true,
  appsflyer_id: true
};

export default function QueryParams() {
  const { set, get } = useFlowState();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    for (const [param, value] of searchParams) {
      // If it isn't a param we care about, skip it
      // NOTE: not using Object.hasOwn due to compatibility concerns
      if (!Object.prototype.hasOwnProperty.call(pickers, param)) {
        continue;
      }

      const sanitizedValue = sanitize(value);
      // if the value is empty, skip it
      if (!sanitizedValue) {
        continue;
      }
      if (typeof pickers[param] === 'function') {
        pickers[param](sanitizedValue, set, get);
      } else if (pickers[param] === true) {
        window.localStorage.setItem(param, sanitizedValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return false;
}
